import { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash.isequal';
import useBeep from './useBeep';

export default (values = {}, options = {}) => {
  const playBeep = useBeep();
  const [itemQuantities, setItemQuantities] = useState(values);
  const valuesRef = useRef(values);
  const optionsRef = useRef(options);
  const valuesIsDifferent = !isEqual(values, valuesRef.current);

  useEffect(() => {
    valuesRef.current = values;
    optionsRef.current = options;
  });

  useEffect(() => {
    if (valuesIsDifferent) {
      setItemQuantities(valuesRef.current);
    }
  }, [valuesIsDifferent]);

  const handleAll = useCallback(
    key => async () => {
      try {
        const quantities = itemQuantities[key] ?? {};
        const { current: currentOptions = {} } = optionsRef;
        const { onBeforeAll, onAll } = currentOptions;
        let newValue = parseInt(quantities.requested, 10);

        if (onBeforeAll && typeof onBeforeAll === 'function') {
          newValue = await onBeforeAll(key, newValue);
        }

        setItemQuantities(oldQuantities => ({
          ...oldQuantities,
          [key]: { ...quantities, quantity: newValue },
        }));

        if (onAll && typeof onAll === 'function') {
          await onAll(key, newValue, {
            ...itemQuantities,
            [key]: { ...quantities, quantity: newValue },
          });
        }
      } catch (err) {
        // Do nothing.
      }
    },
    [itemQuantities],
  );

  const handleChange = useCallback(
    key => async (event, value) => {
      try {
        const quantities = itemQuantities[key] || {};
        const { current: currentOptions = {} } = optionsRef;
        const { onBeforeChange, onChange, allowMoreThanRequested = false } = currentOptions;
        const valueIsNotEmpty = value !== undefined && value !== '';
        let newValue = valueIsNotEmpty ? parseInt(value, 10) : value;

        if (valueIsNotEmpty && newValue > quantities.requested && !allowMoreThanRequested) {
          newValue = quantities.requested;
        }

        if (valueIsNotEmpty && newValue <= 0) {
          newValue = 0;
        }

        if (onBeforeChange && typeof onBeforeChange === 'function') {
          newValue = await onBeforeChange(key, newValue);
        }

        setItemQuantities(oldQuantities => ({
          ...oldQuantities,
          [key]: { ...quantities, quantity: newValue },
        }));

        if (onChange && typeof onChange === 'function') {
          await onChange(key, newValue, {
            ...itemQuantities,
            [key]: { ...quantities, quantity: newValue },
          });
        }
      } catch (err) {
        // Do nothing.
      }
    },
    [itemQuantities],
  );

  const handleMinus = useCallback(
    key => async () => {
      try {
        const quantities = itemQuantities[key] || {};
        const { current: currentOptions = {} } = optionsRef;
        const { onBeforeMinus, onMinus } = currentOptions;

        if (quantities.quantity > 0) {
          let newValue = parseInt(quantities.quantity, 10) - 1;

          if (onBeforeMinus && typeof onBeforeMinus === 'function') {
            newValue = await onBeforeMinus(key, newValue);
          }

          setItemQuantities(oldQuantities => ({
            ...oldQuantities,
            [key]: { ...quantities, quantity: newValue },
          }));

          if (onMinus && typeof onMinus === 'function') {
            await onMinus(key, newValue, {
              ...itemQuantities,
              [key]: { ...quantities, quantity: newValue },
            });
          }
        }
      } catch (err) {
        // Do nothing.
      }
    },
    [itemQuantities],
  );

  const handlePlus = useCallback(
    key => async () => {
      try {
        const quantities = itemQuantities[key] ?? {};
        const { current: currentOptions = {} } = optionsRef;
        const {
          onMaxQuantityReached,
          onBeforePlus,
          onPlus,
          allowMoreThanRequested = false,
          audioFeedback = true,
        } = currentOptions;

        if (quantities.quantity === quantities.requested && !allowMoreThanRequested) {
          if (audioFeedback) {
            playBeep('error');
          }

          if (onMaxQuantityReached && typeof onMaxQuantityReached === 'function') {
            onMaxQuantityReached();
          }

          return;
        }

        let newValue = parseInt((quantities.quantity ?? 0) + 1, 10);

        if (onBeforePlus && typeof onBeforePlus === 'function') {
          newValue = await onBeforePlus(key, newValue);
        }

        setItemQuantities(oldQuantities => ({
          ...oldQuantities,
          [key]: { ...quantities, quantity: newValue },
        }));

        if (audioFeedback) {
          if (
            allowMoreThanRequested ||
            (!allowMoreThanRequested && newValue < quantities.requested)
          ) {
            playBeep('acknowledge');
          }

          if (!allowMoreThanRequested && newValue === quantities.requested) {
            playBeep('success');
          }
        }

        if (onPlus && typeof onPlus === 'function') {
          await onPlus(key, newValue, {
            ...itemQuantities,
            [key]: { ...quantities, quantity: newValue },
          });
        }
      } catch (err) {
        // Do nothing.
      }
    },
    [itemQuantities, playBeep],
  );

  return {
    itemQuantities,
    setItemQuantities,
    handleAll,
    handleChange,
    handleMinus,
    handlePlus,
  };
};
